/**
 * Rocks — three sizes, one shape. See treeOak.ts for the shape of a spec file.
 *
 * THE LADDER (a fine voxel is 0.25 m; a lod-n voxel is 2^n fine):
 *   lod 0        the blob, voxel by voxel, with its grit and its squash
 *   lod 1        small: the same blob FOLDED 2x            large/medium: likewise
 *   lod 2        small: gone                               large/medium: FOLDED 4x
 *   lod 3+       gone
 *
 * FOLDED, NOT STOOD IN, AND UNLIKE A BUILDING THAT IS ALMOST FREE. A stand-in re-authors a model out
 * of primitives, which is the only option for a tree (its trunk is thinner than a lod-1 voxel) and a
 * real cost for a building (a fold regenerates the whole ~95k-sample SDF, per instance, because a
 * building's identity includes its yaw and its seed). A rock has NEITHER: it is not rotated and it
 * reads no seed, so its fold is cached per (type, variant, lod) — three types x four variants x two
 * levels is at most 24 folds for the entire world, ever. And a rock IS a blob, so a `spherePart`
 * stand-in would be the same silhouette with the grit and the squash thrown away.
 *
 * WHY IT ENDS SO EARLY, when a tree is drawn at every representable level. A rock is a ground-hugging
 * bump in the terrain's own colours: it does not break the skyline, which is the whole reason a tree
 * or a building survives out to the far rings. Measured as a radius on the lattice it draws on, a
 * small rock is 3-4 FINE voxels — 1.5-2 voxels at lod 1 and under one at lod 2, i.e. a speck that
 * would read as terrain noise rather than as a rock. Medium and large hold 1.25-2.9 voxels of radius
 * at lod 2 and fall under one at lod 3. So each size stops where it stops being a shape.
 */
import { mat } from '../../../materials/index.js';
import { sphere } from '../../../voxel/blobs.js';
import { StampType } from '../StampTypes.js';
/** Material variation, picked per variant. */
const ROCK_MATERIALS = [mat('rock'), mat('rock2'), mat('rock_moss')];
/**
 * Grit amplitude and weight ramp — the two numbers that make a rock a rock rather than a ball. The
 * ramp is softer than the blob emitter's default (0.4 against 0.5), which is what keeps the chipped
 * surface from packing flat. See voxel/blobs.sphere.
 */
const ROCK_GRIT = 0.4;
const ROCK_RAMP = 0.4;
/** Radius in fine voxels, per size. The one number that separates the three types. */
const RADIUS = {
    [StampType.ROCK_SMALL]: (v) => 3 + (v % 3) * 0.5,
    [StampType.ROCK_MEDIUM]: (v) => 5 + (v % 3) * 0.8,
    [StampType.ROCK_LARGE]: (v) => 8 + (v % 4) * 1.2,
};
function params(type) {
    return (variant) => {
        const radius = RADIUS[type](variant);
        return {
            radius,
            material: ROCK_MATERIALS[variant % ROCK_MATERIALS.length],
            yScale: 0.9 + (variant % 3) * 0.15,
            sink: Math.floor(radius * 0.15),
        };
    };
}
/** LOD 0 — the original generateRock body, now a single call to the blob emitter. */
function lod0(p, variant) {
    return sphere(0, -p.sink, 0, p.radius, p.material, ROCK_GRIT, variant, {
        yScale: p.yScale,
        noise: 'grit',
        ramp: ROCK_RAMP,
    });
}
/**
 * `maxLod` = `foldLod` for all three, which is how a spec says "I have no stand-in": above the fold
 * levels there is no `lodParts` to fall back to and the registry returns null.
 */
function rockSpec(type, maxLod) {
    return { type, maxLod, foldLod: maxLod, params: params(type), lod0 };
}
export const ROCK_SMALL_SPEC = rockSpec(StampType.ROCK_SMALL, 1);
export const ROCK_MEDIUM_SPEC = rockSpec(StampType.ROCK_MEDIUM, 2);
export const ROCK_LARGE_SPEC = rockSpec(StampType.ROCK_LARGE, 2);
//# sourceMappingURL=rock.js.map